import { useEffect, useState, useCallback } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { Col, Button, Dropdown, Row, Tooltip } from "antd";
import { DownOutlined, UserOutlined } from "@ant-design/icons";
import { BiWorld, BiSolidBadge } from "react-icons/bi";
import { useTranslation } from "react-i18next";
import { VERIFICATION_BADGE_CONFIG } from "components/universal/ObligationDetails/components/widgets/ODRecurrenceTimeline";
import { logout as authLogout } from "auth";
import { preloadNamespacesForRoute, preloadRemainingNamespaces } from "i18n";
import { serviceSwitchDataBase } from "api/services/defaultServices";
import { getDefaultAuthenticatedPath, canAccessModule } from "modules/appModuleAccess";
import { resolveBackgroundColorValue, isColorDark } from "utils/styleUtils";
import { getObligationsPendingVerificationCount } from "api/services/obligationsServices";
import { APP_MODULES_CONFIG } from "utils/features";
import { APP_MODULE_KEYS } from "modules/appModuleKeys";
import useAuthStore from "stores/useAuthStore";
import GlobalSearchBarDesign from "./GlobalSearchBarDesign";
import UserMenu from "./UserMenu";
import "./Header.css";

const AppHeaderBadgeBtn = ({ count, tooltipTitle, loading, isDarkBackground }) => {
  const navigate = useNavigate();

  if (loading) {
    return (
      <span
        className={[
          "app-header-badge-skeleton",
          isDarkBackground ? "app-header-badge-skeleton--on-dark" : "",
        ].filter(Boolean).join(" ")}
        aria-hidden="true"
      />
    );
  }

  const display = count > 99 ? "99+" : String(count);
  const countClasses = [
    "app-header-badge-btn-count",
    // count < 10 ? "app-header-badge-btn-count--small-count" : "",
    // count > 99 ? "app-header-badge-btn-count--large-count" : "",
  ].filter(Boolean).join(" ");

  const isAllApproved = count === 0;
  const approvedConfig = VERIFICATION_BADGE_CONFIG["evidence accepted"];

  return (
    <Tooltip title={tooltipTitle} placement="bottom" destroyTooltipOnHide>
      <button
        className={"app-header-badge-btn"}
        onClick={() => navigate("/obligation_approval")}
        aria-label={isAllApproved ? "All approved — navigate to route" : `${display} items — navigate to route`}
      >
        <div className="app-header-badge-btn-shape-wrapper">
          <BiSolidBadge
            className="app-header-badge-btn-shape"
            style={isAllApproved ? { color: approvedConfig.bg, filter: `drop-shadow(0 0 2px ${approvedConfig.bg})` } : undefined}
          />

          {isAllApproved ? (
            <span
              className="app-header-badge-btn-check"
              style={{ color: approvedConfig.font }}
            >
              {approvedConfig.icon}
            </span>
          ) : (
            <span className={countClasses}>{display}</span>
          )}
        </div>
      </button>
    </Tooltip>
  );
};

const IS_OFFLINE = process.env.REACT_APP_MODE_OFFLINE === "true";

const DEFAULT_HEADER_BRAND_NAME = "HORUZ";
const DEFAULT_HEADER_BACKGROUND_COLOR = "#ffffff";
const DB_LABELS = {
  NATIONAL: "Hospitales",
  INTERNATIONAL: "Internacional",
  EMCASA: "EMCASA",
  CSG: "CSG",
};
const DB_MENU_ITEMS = [
  { key: "NATIONAL", label: DB_LABELS.NATIONAL },
  { key: "INTERNATIONAL", label: DB_LABELS.INTERNATIONAL },
  { key: "EMCASA", label: DB_LABELS.EMCASA },
  { key: "CSG", label: DB_LABELS.CSG },
];
const LANGUAGE_LABELS = {
  en: "English",
  en_proman: "English",
  es: "Español",
  es_emcasa: "EMCASA",
  es_csg: "CSG",
};
const LANGUAGE_MENU_ITEMS = [
  { key: "en", label: LANGUAGE_LABELS.en },
  { key: "en_proman", label: LANGUAGE_LABELS.en_proman },
  { key: "es", label: LANGUAGE_LABELS.es },
  { key: "es_emcasa", label: LANGUAGE_LABELS.es_emcasa },
  { key: "es_csg", label: LANGUAGE_LABELS.es_csg },
];

/**
 * Returns env header url, env header brand name or the default
 */
function resolveHeaderBranding() {
  const brandName = process.env.REACT_APP_HEADER_BRAND_NAME?.trim();

  // In offline mode the brand name is prioritized over the logo since we can't
  // actually fetch the image and the value that could get configured is always the brand name
  if (IS_OFFLINE) {
    if (brandName) return { type: "name", value: brandName };
    return { type: "logo", value: `/assets/images/149MKM_trans_blue.png?ts=${Date.now()}` };
  }

  // In online mode we prioritize the logo if configured, falling back to the brand name and then to the default brand name
  const logoUrl = process.env.REACT_APP_HEADER_LOGO_URL?.trim();
  if (logoUrl) return { type: "logo", value: logoUrl };

  if (brandName) return { type: "name", value: brandName };

  return { type: "name", value: DEFAULT_HEADER_BRAND_NAME };
};

const headerBranding = resolveHeaderBranding();
const headerBackgroundColor = resolveBackgroundColorValue(
  process.env.REACT_APP_HEADER_BACKGROUND_COLOR,
  DEFAULT_HEADER_BACKGROUND_COLOR
);
const isHeaderBackgroundDark = isColorDark(headerBackgroundColor);

const availableLanguages = (process.env.REACT_APP_AVAILABLE_LANGUAGES || "en")
  .split(",")
  .map((lang) => lang.trim())
  .filter(Boolean);

const languageMenuItems = LANGUAGE_MENU_ITEMS.filter((item) =>
  availableLanguages.includes(item.key)
);

const resolveConfiguredLanguage = (storedLanguage) => {
  const preferredLanguage = process.env.REACT_APP_PREFERRED_LANGUAGE || "en";
  const fallbackLanguage = availableLanguages.includes(preferredLanguage)
    ? preferredLanguage
    : availableLanguages[0] || "en";

  return availableLanguages.includes(storedLanguage) ? storedLanguage : fallbackLanguage;
};

/**
 * @param {Function} onLogout Function to logout user
 * @param {Boolean} hideHeaderTools If true hides the global search
 */
const Header = ({ onLogout, hideHeaderTools }) => {
  const { t, i18n } = useTranslation("common");
  const location = useLocation();
  const userData = useAuthStore((s) => s.userData);
  const setLoadingDB = useAuthStore((s) => s.setLoadingDB);
  const setDBInfo = useAuthStore((s) => s.setDBInfo);

  const [dbName, setDBName] = useState("INTERNATIONAL");
  const [language, setLanguage] = useState(i18n.language || "en");
  const [pendingVerificationCount, setPendingVerificationCount] = useState(null);
  // const [isLoadingPendingVerificationCount, setLoadingPendingVerificationCount] = useState(false);

  const canAccessVerification = canAccessModule(APP_MODULE_KEYS.VERIFICATIONS, userData);

  const fetchPendingVerificationCount = async () => {
    // setLoadingPendingVerificationCount(true);
    try {
      const response = await getObligationsPendingVerificationCount();
      // This count being 0 means this user has no assigned obligations for verification
      if (response?.groups_count === 0) return;
      setPendingVerificationCount(response?.count ?? 0);
    } catch (error) {
      console.error("Error fetching pending verification count:", error);
    } finally {
      // setLoadingPendingVerificationCount(false);
    }
  };

  useEffect(() => {
    if (!canAccessVerification) return;
    fetchPendingVerificationCount();
  }, [canAccessVerification]);

  const syncDB = useCallback(() => {
    const dbChoice = localStorage.getItem("dbChoice");
    if (dbChoice) {
      setDBInfo(DB_LABELS[dbChoice]);
      setDBName(DB_LABELS[dbChoice]);
    } else {
      setDBInfo(DB_LABELS.INTERNATIONAL);
      setDBName(DB_LABELS.INTERNATIONAL);
    }
  }, [setDBInfo]);

  const changeLanguageForCurrentRoute = useCallback(
    async (nextLanguage) => {
      setLanguage(nextLanguage);
      localStorage.setItem("i18nextLng", nextLanguage);

      try {
        await preloadNamespacesForRoute(location.pathname, nextLanguage);
        await i18n.changeLanguage(nextLanguage);
        void preloadRemainingNamespaces(nextLanguage);
      } catch (error) {
        console.error("Language change failed:", error);
        await i18n.changeLanguage(nextLanguage);
      }
    },
    [i18n, location.pathname]
  );

  const syncLanguage = useCallback(() => {
    const langChoice = localStorage.getItem("i18nextLng");
    const nextLanguage = resolveConfiguredLanguage(langChoice);
    setLanguage(nextLanguage);
    localStorage.setItem("i18nextLng", nextLanguage);
    if (i18n.language !== nextLanguage) {
      void changeLanguageForCurrentRoute(nextLanguage);
    }
  }, [changeLanguageForCurrentRoute, i18n.language]);

  useEffect(() => {
    syncDB();
    syncLanguage();
  }, [syncDB, syncLanguage]);

  const handleMenuClick = useCallback(
    (e) => {
      void changeLanguageForCurrentRoute(e.key);
    },
    [changeLanguageForCurrentRoute]
  );

  const handleMenuDatabase = useCallback(
    async (e) => {
      const selectedDB = e.key;
      try {
        setLoadingDB(true);
        await serviceSwitchDataBase(selectedDB);
        localStorage.setItem("dbChoice", selectedDB);
        authLogout({ setExpired: false });
        window.location.href = "/login";
      } catch (error) {
        console.error(error);
      } finally {
        setLoadingDB(false);
      }
    },
    [setLoadingDB]
  );

  return (
    <div
      className="app-header-container"
      data-theme={isHeaderBackgroundDark ? "dark" : "light"}
      style={{
        background: headerBackgroundColor
      }}
    >
      <Row justify="space-between" align="middle" wrap={false}>
        <Col flex="0 0 auto">
          <NavLink
            className="app-header-home-link"
            to={getDefaultAuthenticatedPath(userData)}
          >
            {headerBranding.type === "logo" ? (
              <img
                src={headerBranding.value}
                alt="Brand logo"
                className="app-header-brand-logo"
              />
            ) : (
              <h2 className="app-header-brand-name">{headerBranding.value}</h2>
            )}
          </NavLink>
        </Col>

        <Col flex="1 1 auto" className="app-header-search-col">
          <div className="app-header-search-wrapper">
            {!hideHeaderTools && (
              <GlobalSearchBarDesign />
            )}

            {(canAccessVerification && pendingVerificationCount != null) && (
              <AppHeaderBadgeBtn
                count={pendingVerificationCount}
                tooltipTitle={t("obligations_pending_verification")}
                // loading={isLoadingPendingVerificationCount}
                isDarkBackground={isHeaderBackgroundDark}
              />
            )}
          </div>
        </Col>

        {/* 
        {canAccessVerification && (
          <Col flex="0 0 auto">
            <AppHeaderBadgeBtn count={5} tooltipTitle={t("obligations_pending_verification")} />
          </Col>
        )} */}

        <Col flex="0 0 auto">
          <div className="app-header-right">
            {APP_MODULES_CONFIG.modeDemo && (
              <Dropdown
                menu={{ items: DB_MENU_ITEMS, onClick: handleMenuDatabase }}
                trigger={["click"]}
              >
                <Button type="text" className="app-header-dropdown-button">
                  {dbName}
                  <DownOutlined className="app-header-dropdown-icon" />
                </Button>
              </Dropdown>
            )}

            <Dropdown
              menu={{ items: languageMenuItems, onClick: handleMenuClick }}
              trigger={["click"]}
            >
              <Button type="text" className="app-header-dropdown-button">
                <BiWorld size={20} className="app-header-language-icon" />
                {LANGUAGE_LABELS[language] ?? language}
                <DownOutlined className="app-header-dropdown-icon" />
              </Button>
            </Dropdown>

            <UserMenu
              onLogout={onLogout}
              customTriggerRender={
                <Button type="text" className="app-header-dropdown-button">
                  <UserOutlined style={{ fontSize: 20 }} />
                </Button>
              }
            />
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default Header;